<div class="relative min-h-screen overflow-hidden bg-[#fffcf9]">
  <main class="relative mx-auto max-w-5xl space-y-6 px-4 py-10 sm:px-6 lg:space-y-8">

    <!-- Profile header -->
    <section class="profile-card flex flex-col items-center gap-5 rounded-3xl border border-stone-200 bg-white p-6 text-center shadow-sm sm:flex-row sm:p-8 sm:text-left">
      <div class="avatar-pop flex h-20 w-20 shrink-0 items-center justify-center rounded-full bg-[#f0e8fc] text-2xl font-semibold tracking-wide text-[#7439e6]">
        {{ initials }}
      </div>

      <div class="min-w-0 flex-1">
        <h1 class="truncate font-sans text-2xl font-medium text-stone-900 sm:text-3xl">
          {{ userDetails?.name || 'Your profile' }}
        </h1>
        <p class="mt-1 truncate text-sm text-stone-500">{{ userDetails?.email }}</p>

        <div class="mt-3 flex flex-wrap items-center justify-center gap-2 sm:justify-start">
          <span class="rounded-full bg-[#f5f1fb] px-3 py-1 text-xs font-semibold text-[#7439e6]">
            {{ userDetails?.subsctions?.membershipType || userDetails?.isUserType || 'Basic' }} member
          </span>
          <span class="rounded-full px-3 py-1 text-xs font-semibold"
                [ngClass]="userDetails?.isApproved ? 'bg-stone-100 text-stone-600' : 'bg-amber-50 text-amber-700'">
            {{ userDetails?.isApproved ? 'Approved' : 'Approval pending' }}
          </span>
          @if (userDetails?.createdAt) {
            <span class="rounded-full bg-stone-100 px-3 py-1 text-xs font-medium text-stone-600">
              Member since {{ userDetails.createdAt | date:'MMM yyyy' }}
            </span>
          }
        </div>
      </div>
    </section>

    <div class="grid grid-cols-1 items-start gap-6 lg:grid-cols-5 lg:gap-8">

      <!-- Left column: personal details + advisor -->
      <div class="space-y-6 lg:col-span-2 lg:space-y-8">
        <section class="profile-card rounded-3xl border border-stone-200 bg-white p-6 shadow-sm sm:p-8" style="animation-delay: 80ms">
          <form [formGroup]="profileForm" (ngSubmit)="saveProfile()">
            <h2 class="font-sans text-xl font-medium text-stone-900">Profile Details</h2>
            <p class="mt-1 text-xs font-semibold uppercase tracking-[0.12em] text-stone-500">Personal Information</p>

            <div class="mt-6 space-y-4">
              <label class="block">
                <span class="mb-1.5 block text-xs font-medium text-stone-500">Full name</span>
                <input type="text" formControlName="name" [value]="userDetails?.name || ''"
                  placeholder="Full Name"
                  class="w-full rounded-xl border border-stone-200 bg-[#fffcf9] px-4 py-3 text-sm font-medium text-stone-900 outline-none transition-all duration-200 placeholder:text-stone-400 focus:border-[#8c52ff] focus:bg-white focus:ring-4 focus:ring-[#8c52ff]/15">
              </label>

              <label class="block">
                <span class="mb-1.5 block text-xs font-medium text-stone-500">Email address</span>
                <input type="email" formControlName="email" [value]="userDetails?.email || ''"
                  placeholder="Email Address"
                  class="w-full rounded-xl border border-stone-200 bg-[#fffcf9] px-4 py-3 text-sm font-medium text-stone-900 outline-none transition-all duration-200 placeholder:text-stone-400 focus:border-[#8c52ff] focus:bg-white focus:ring-4 focus:ring-[#8c52ff]/15">
              </label>

              <div>
                <span class="mb-1.5 block text-xs font-medium text-stone-500">Phone number</span>
                <div class="flex overflow-hidden rounded-xl border border-stone-200 bg-stone-100">
                  <span class="flex items-center gap-2 border-r border-stone-200 px-4 text-sm font-semibold text-stone-700">
                    <span class="text-lg">{{ userDetails?.country?.flag || '🇮🇳' }}</span>
                    {{ userDetails?.country?.dial || '+91' }}
                  </span>
                  <input type="text" formControlName="phone" [value]="userDetails?.phone || ''"
                    placeholder="Phone Number"
                    class="w-full cursor-not-allowed bg-transparent px-4 py-3 text-sm font-medium text-stone-500 outline-none">
                </div>
                <p class="mt-1.5 text-xs text-stone-400">Your phone number can't be changed.</p>
              </div>
            </div>

            <button type="submit"
                    class="mt-6 inline-flex w-full items-center justify-center rounded-xl bg-[#7439e6] px-8 py-3 text-sm font-semibold text-white transition-all duration-200 hover:bg-[#6330c7] focus:outline-none focus-visible:ring-4 focus-visible:ring-[#8c52ff]/30 active:translate-y-0 active:scale-[0.99]">
              Save Changes
            </button>
          </form>
        </section>

        <!-- Assigned advisor -->
        @if (userDetails?.assignedAdvisor; as advisor) {
          <section class="profile-card rounded-3xl border border-stone-200 bg-white p-6 shadow-sm sm:p-8" style="animation-delay: 240ms">
            <p class="text-xs font-semibold uppercase tracking-[0.12em] text-stone-500">Your Advisor</p>

            <div class="mt-4 flex items-center gap-4">
              @if (advisor.image) {
                <img [src]="advisor.image" [alt]="advisor.name" class="h-14 w-14 shrink-0 rounded-full object-cover">
              } @else {
                <div class="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-[#f0e8fc] text-lg font-semibold text-[#7439e6]">
                  {{ advisor.name?.charAt(0) }}
                </div>
              }
              <div class="min-w-0">
                <p class="truncate text-base font-semibold text-stone-900">{{ advisor.name }}</p>
                <p class="truncate text-sm text-stone-500">{{ advisor.specialization }}</p>
              </div>
            </div>

            <div class="mt-5 grid grid-cols-2 gap-3">
              @if (advisor.phone) {
                <a [href]="'tel:' + advisor.phone"
                   class="inline-flex items-center justify-center gap-2 rounded-xl border border-stone-200 px-4 py-2.5 text-sm font-semibold text-stone-700 transition-all duration-200 hover:border-[#8c52ff]/40 hover:bg-[#f7f3fc] hover:text-[#7439e6]">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-4 w-4" aria-hidden="true">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
                  </svg>
                  Call
                </a>
              }
              @if (advisor.email) {
                <a [href]="'mailto:' + advisor.email"
                   class="inline-flex items-center justify-center gap-2 rounded-xl border border-stone-200 px-4 py-2.5 text-sm font-semibold text-stone-700 transition-all duration-200 hover:border-[#8c52ff]/40 hover:bg-[#f7f3fc] hover:text-[#7439e6]">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-4 w-4" aria-hidden="true">
                    <rect x="3" y="5" width="18" height="14" rx="2" />
                    <path d="M3 7l9 6 9-6" />
                  </svg>
                  Email
                </a>
              }
            </div>
          </section>
        }
      </div>

      <!-- Right column: membership -->
      <div class="lg:col-span-3">

        <!-- For Premium User -->
        <section *ngIf="userDetails?.isUserType === 'Premium'"
                 class="profile-card overflow-hidden rounded-3xl border border-stone-200 bg-white shadow-sm" style="animation-delay: 160ms">
          <!-- Plan band, themed like the plan cards -->
          <div class="plan-band px-6 pb-7 pt-6 sm:px-8">
            <div class="flex items-start justify-between gap-4">
              <div>
                <p class="text-xs font-semibold uppercase tracking-[0.12em]">
                  Active Plan
                </p>
                <h2 class="mt-2 text-sm font-semibold uppercase tracking-[0.12em]">
                  {{ userDetails?.subsctions?.membershipType || 'Premium' }}
                </h2>
              </div>
              <span class="inline-flex items-center gap-1.5 rounded-full bg-white/85 px-3 py-1 text-xs font-semibold capitalize"
                    [ngClass]="isSubscriptionActive ? 'text-[#7439e6]' : 'text-stone-600'">
                <span class="h-2 w-2 rounded-full" [ngClass]="isSubscriptionActive ? 'bg-[#8c52ff]' : 'bg-stone-400'"></span>
                {{ userDetails?.subsctions?.subscriptionStatus || '—' }}
              </span>
            </div>

            @if (membershipPlan; as plan) {
              <p class="mt-3 flex flex-wrap items-baseline gap-x-1.5">
                <span class="text-3xl font-semibold tracking-tight">₹{{ plan.price | number }}</span>
                <span class="text-sm font-medium">{{ plan.billingPeriod }}</span>
              </p>
              <p class="mt-2 text-sm">{{ plan.tagline }}</p>
            }
          </div>

          <div class="space-y-7 p-6 sm:p-8">
            <!-- Time remaining -->
            @if (daysLeft !== null) {
              <div>
                <div class="flex items-baseline justify-between text-sm">
                  <span class="font-semibold text-stone-900">{{ daysLeft }} {{ daysLeft === 1 ? 'day' : 'days' }} left</span>
                  <span class="text-stone-500">Expires {{ userDetails?.subsctions?.subscriptionEndDate | date:'dd MMM yyyy' }}</span>
                </div>
                <div class="mt-2 h-2 overflow-hidden rounded-full bg-stone-100">
                  <div class="progress-fill h-full rounded-full bg-[#8c52ff]" [style.width.%]="100 - subscriptionProgress"></div>
                </div>
              </div>
            }

            <!-- Subscription facts -->
            <dl class="grid grid-cols-2 gap-x-6 gap-y-5">
              <div>
                <dt class="text-[11px] font-semibold uppercase tracking-wider text-stone-400">Purchased on</dt>
                <dd class="mt-1 text-sm font-semibold text-stone-900">
                  {{ (userDetails?.subsctions?.subscriptionStartDate | date:'dd MMM yyyy') || '—' }}
                </dd>
              </div>
              <div>
                <dt class="text-[11px] font-semibold uppercase tracking-wider text-stone-400">Expires on</dt>
                <dd class="mt-1 text-sm font-semibold text-stone-900">
                  {{ (userDetails?.subsctions?.subscriptionEndDate | date:'dd MMM yyyy') || '—' }}
                </dd>
              </div>
              <div>
                <dt class="text-[11px] font-semibold uppercase tracking-wider text-stone-400">Plan Type</dt>
                <dd class="mt-1 text-sm font-semibold text-stone-900">
                  {{ userDetails?.subsctions?.membershipType || '—' }}
                </dd>
              </div>
              <div>
                <dt class="text-[11px] font-semibold uppercase tracking-wider text-stone-400">Status</dt>
                <dd class="mt-1 text-sm font-semibold capitalize text-stone-900">
                  {{ userDetails?.subsctions?.subscriptionStatus || '—' }}
                </dd>
              </div>
              <div class="col-span-2">
                <dt class="text-[11px] font-semibold uppercase tracking-wider text-stone-400">Transaction ID</dt>
                <dd class="mt-1 break-all text-sm font-semibold text-stone-900">
                  {{ userDetails?.subsctions?.lastTransactionId || '—' }}
                </dd>
              </div>
            </dl>

            <!-- What the plan includes -->
            @if (membershipPlan; as plan) {
              <div class="border-t border-stone-100 pt-6">
                <p class="text-xs font-semibold uppercase tracking-[0.12em] text-stone-500">Your plan includes</p>
                <ul class="mt-3 grid grid-cols-1 gap-x-6 gap-y-2.5 sm:grid-cols-2">
                  @for (feature of plan.features; track feature.label; let i = $index) {
                    <li class="feature-row flex items-start gap-2.5 text-sm" [style.animation-delay.ms]="300 + i * 60">
                      @if (feature.included) {
                        <span class="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#8c52ff] text-white">
                          <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" class="h-3 w-3" aria-hidden="true">
                            <path d="M5 10.5l3.2 3.2L15 7" />
                          </svg>
                        </span>
                      } @else {
                        <span class="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-stone-100 text-stone-400">
                          <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" class="h-3 w-3" aria-hidden="true">
                            <path d="M6 6l8 8M14 6l-8 8" />
                          </svg>
                        </span>
                      }
                      <span [class]="feature.included ? 'font-medium text-stone-800' : 'text-stone-400'">
                        {{ feature.label }}
                        <span class="sr-only">{{ feature.included ? '(included)' : '(not included)' }}</span>
                      </span>
                    </li>
                  }
                </ul>
              </div>
            }

            <button (click)="downloadReceipt()"
                    class="group inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#7439e6] px-8 py-3.5 text-sm font-semibold text-white transition-all duration-200 hover:bg-[#6330c7] focus:outline-none focus-visible:ring-4 focus-visible:ring-[#8c52ff]/30 active:translate-y-0 active:scale-[0.99]">
              <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                   class="h-4 w-4 transition-transform duration-200 group-hover:translate-y-0.5" aria-hidden="true">
                <path d="M10 3v10M6 9l4 4 4-4M4 16h12" />
              </svg>
              Download Invoice
            </button>
          </div>
        </section>

        <!-- For Basic User -->
        <section *ngIf="userDetails?.isUserType !== 'Premium'"
                 class="profile-card overflow-hidden rounded-3xl border border-stone-200 bg-white" style="animation-delay: 160ms">
          <div class="plan-band px-6 pb-7 pt-6 sm:px-8">
            <p class="text-xs font-semibold uppercase tracking-[0.12em] text-stone-500">Active Plan</p>
            <h2 class="mt-2 text-sm font-semibold uppercase tracking-[0.12em] text-stone-700">Basic</h2>
            <p class="mt-3 text-3xl font-semibold tracking-tight text-stone-800">Free Plan</p>
            <p class="mt-2 text-sm text-stone-500">View up to {{ viewLimit }} premium properties</p>
          </div>

          <div class="space-y-7 p-6 sm:p-8">
            <div>
              <div class="flex items-baseline justify-between text-sm">
                <span class="font-semibold text-stone-900">{{ viewedCount }} of {{ viewLimit }} property views used</span>
              </div>
              <div class="mt-2 h-2 overflow-hidden rounded-full bg-stone-100">
                <div class="progress-fill h-full rounded-full bg-[#8c52ff]" [style.width.%]="viewsProgress"></div>
              </div>
            </div>

            <div class="flex flex-col items-center gap-3 text-center">
              <button routerLink="/payment"
                      class="group inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#7439e6] px-8 py-3.5 text-sm font-semibold text-white transition-all duration-200 hover:bg-[#6330c7] focus:outline-none focus-visible:ring-4 focus-visible:ring-[#8c52ff]/30 active:translate-y-0 active:scale-[0.99]">
                Upgrade your plan
                <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                     class="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true">
                  <path d="M4 10h12M11 5l5 5-5 5" />
                </svg>
              </button>
              <small class="text-stone-500">Unlock 300+ curated properties — plans from ₹5,000 / 90 days</small>
            </div>
          </div>
        </section>
      </div>
    </div>
  </main>
</div>
